import React, { useState, useEffect, useCallback, useContext, useRef } from "react";
import PropTypes from "prop-types";
import { Modal, Icon, Skeleton, Popover } from "antd";
import LeftOrdersPanel from "./LeftOrdersPanel";
import RightOrderDetailsPanel from "./RightOrderDetailsPanel";
import { formatFullName, renderAlertMessage } from "../../../helpers/common";
import {
  getInvoiceOrderCharges,
  onCreateAccessorialInvoiceLine,
  onUpdateAccessorialInvoiceLine,
} from "../../../api/Billing";
import { alertMessage } from "../../../common/Common";
import { Button } from "antd";
import { sendInvoiceEDIStatus } from "../../../api/BillingScreenAPI";
import { fetchAccessorialsComponents, fetchAccountAccessorials } from "../../../api/Accessorials";
import { UserContext } from "../../../context/UserContext";
import Button_v2 from "../../../stories/components/Button_v2";
import { fetchDriverList } from "../../../api/UsersApi";

const getOrderNumber = order =>
  order && (order.customer_order_number || order.order_number || order.id || order._id);

const InvoiceChargesContainer = ({
  invoice_orders = [],
  accountInvoiceId = null,
  currentOrg = {},
  accountId = "",
  callEmailApi = () => {},
  isFetchingDetails = false,
  onChargesModified = () => {}, // NEW - bubbles up to ShowInvoice
}) => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [currentOrderDetails, setCurrentOrderDetails] = useState(null);
  const [orderDetailsMap, setOrderDetailsMap] = useState({}); // key: customer_order_id --> cached data
  const [currentOrderCharges, setCurrentOrderCharges] = useState(null);
  const [loading, setLoading] = useState(false);
  const [setShowEmailModal, showEmailModal] = useState(false);
  const [sendingEdiInvoice, setSendingEdiInvoice] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [isFetchingDrivers, setIsFetchingDrivers] = useState(false);
  const [rebillingProgress, setRebillingProgress] = useState(false); // NEW - to indicate rebill creation in progress
  //setting accessorial data
  const [accountComponentAccessorial, setAccountComponentAccessorial] = useState([]);
  const [displayComponents, setDisplayComponents] = useState([]);
  const [noParamComponents, setNoParamComponents] = useState([]);
  const [orgComponents, setOrgComponents] = useState([]);
  const [isGlobalEditMode, setIsGlobalEditMode] = useState(false);
  const userContext = useContext(UserContext);
  const { orgRules } = userContext;
  const showZeroInvLines = _.get(orgRules, "show_zero_invoice_lines", false) === "true";

  // --- Unsaved changes guard state ---
  const rightPanelRef = useRef(null);
  const hasPendingChangesRef = useRef(false);
  const [isUnsavedGuardVisible, setIsUnsavedGuardVisible] = useState(false);
  const [pendingTargetOrder, setPendingTargetOrder] = useState(null);
  const [isGuardBusy, setIsGuardBusy] = useState(false);

  const [hasInvalidCharges, setHasInvalidCharges] = useState(false);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  const currentOrderId = selectedOrder
    ? selectedOrder.customer_order_id || selectedOrder.id || selectedOrder._id
    : null;
  const hasInitializedRef = useRef(false);
  const isZeroInvValue = payload => Number(payload.invoice_value) === 0; // checking non zero for accessorial charge.

  const fetchInvoiceCharges = useCallback(
    async order => {
      if (!order) return;
      setLoading(true);
      const customerOrderId = order.customer_order_id;
      try {
        const payload = {
          customer_order_id: customerOrderId,
          account_invoice_id: accountInvoiceId,
          organization_id: currentOrg.id,
        };
        const response = await getInvoiceOrderCharges(payload);

        if (response?.success) {
          const details = response.orderDetails || {};
          setCurrentOrderDetails(details);
          setCurrentOrderCharges(response.chargesDetails || {});

          // Cache it by customer_order_id so other cards keep their own fetched amount
          const key = details.customer_order_id || customerOrderId;
          setOrderDetailsMap(prev => ({
            ...prev,
            [key]: details,
          }));
        } else if (response?.errors) {
          renderAlertMessage(response.errors);
          setCurrentOrderDetails({});
          setCurrentOrderCharges({});
        }
      } catch (err) {
        renderAlertMessage(err?.message || "Failed to load order charges");
        setCurrentOrderDetails({});
        setCurrentOrderCharges({});
      } finally {
        setLoading(false);
      }
    },
    [accountInvoiceId, currentOrg]
  );

  // Select the first order on initial mount and fetch its charges
  // useEffect(() => {
  //   if (invoice_orders && invoice_orders.length > 0) {
  //     const firstOrder = invoice_orders[0];
  //     setSelectedOrder(firstOrder);
  //     fetchInvoiceCharges(firstOrder);
  //     getOrgComponents();
  //     getDrivers();
  //   }
  // }, []);

  // Called whenever RightOrderDetailsPanel successfully saves charges
  const handleChargesModified = () => {
    onChargesModified(true);
  };

  useEffect(() => {
    // Still loading from parent — don't do anything yet
    if (isFetchingDetails) return;

    // Loaded but empty — nothing to select
    if (!invoice_orders || invoice_orders.length === 0) return;

    // Only auto-select the first order once, right after loading finishes
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    const firstOrder = invoice_orders[0];
    setSelectedOrder(firstOrder);
    fetchInvoiceCharges(firstOrder);
    getOrgComponents();
    getDrivers();
  }, [isFetchingDetails, invoice_orders]);

  useEffect(() => {
    if (orgComponents.length) fetchAccountComponentAccessorials();
  }, [orgComponents]);

  // Actually switches the selected order + fetches its charges.
  const switchToOrder = order => {
    setSelectedOrder(order);
    fetchInvoiceCharges(order);
  };

  // Entry point used by LeftOrdersPanel. Guards the switch if the
  // currently open order has unsaved charge changes.

  const handleOrderSelect = order => {
    const targetId = order._id || order.id;
    const currentId = selectedOrder ? selectedOrder._id || selectedOrder.id : null;

    if (targetId === currentId) return;
    
    if (rebillingProgress) return; // Don't allow switching orders while a rebill is being creating..

    if (hasPendingChangesRef.current) {
      // If there are invalid charges, don't show the popup.
      // Just switch to the selected order.
      if (hasInvalidCharges) {
        switchToOrder(order);
        handleDiscardAndSwitch();
        return;
      }

      setPendingTargetOrder(order);
      setIsUnsavedGuardVisible(true);
      return;
    }

    switchToOrder(order);
  };
  
  const handleDoneEditing = () => {
    if (hasPendingChanges || hasInvalidCharges) return; // guarded by disabled button anyway
    setIsGlobalEditMode(false);
  };

  const closeUnsavedGuard = () => {
    if (isGuardBusy) return;
    setIsUnsavedGuardVisible(false);
    setPendingTargetOrder(null);
  };

  // "Discard" from the Unsaved Changes guard modal.
  const handleDiscardAndSwitch = () => {
    if (rightPanelRef.current) {
      rightPanelRef.current.discardPendingChanges();
    }
    const target = pendingTargetOrder;
    setIsUnsavedGuardVisible(false);
    setPendingTargetOrder(null);
    if (target) switchToOrder(target);
  };

  // "Save" from the Unsaved Changes guard modal — delegates to the
  // RightOrderDetailsPanel's own Update Remark flow, then switches
  // orders only if that save actually succeeded.
  const handleSaveAndSwitch = async () => {
    if (!rightPanelRef.current) return;

    // Close the Unsaved Changes modal immediately
    setIsUnsavedGuardVisible(false);
    setIsGuardBusy(true);
    try {
      const saved = await rightPanelRef.current.requestSaveForSwitch();
      if (saved) {
        const target = pendingTargetOrder;
        setPendingTargetOrder(null);

        if (target) {
          switchToOrder(target);
        }
      } else {
        // User cancelled or save failed.
        // Keep the current order selected.
        setPendingTargetOrder(null);
      }
    } finally {
      setIsGuardBusy(false);
    }
  };

  const handleEnableGlobalEditMode = () => {
    setIsGlobalEditMode(true);
  };

  const handleDisableGlobalEditMode = () => {
    setIsGlobalEditMode(false);
  };

  //fetch Drivers
  const getDrivers = async () => {
    const orgId = currentOrg?.id || "";

    setIsFetchingDrivers(true);

    try {
      const result = await fetchDriverList({
        unscoped: true,
        orgId,
        includeWH: true,
      });

      if (result.success && result?.data?.drivers) {
        const allDrivers = (result.data.drivers || []).map(driver => ({
          id: driver.id,
          employee_code: driver.employee_code,
          full_name: formatFullName(driver),
          deleted_at: driver.deleted_at,
          first_name: driver.first_name,
          last_name: driver.last_name,
        }));

        setDrivers(allDrivers);
      } else if (result.errors) {
        renderAlertMessage(result.errors);
        setDrivers([]);
      }
    } catch (error) {
      alertMessage(error.message, "error", 10);
    } finally {
      setIsFetchingDrivers(false);
    }
  };

  //fetching orgComponents
  const getOrgComponents = async () => {
    try {
      setLoading(true);

      const result = await fetchAccessorialsComponents();

      if (result.success) {
        const orgComponentsResponse = result.components || [];
        const displayComponents = [];
        const noParamComponents = [];

        orgComponentsResponse.forEach(component => {
          if (component.component_params && component.component_params.length > 0) {
            const visibleParams = component.component_params.filter(param =>
              param.visible_to.includes("DISPATCHER")
            );

            if (visibleParams.length > 0) {
              displayComponents.push(component.code);
            } else {
              noParamComponents.push(component.code);
            }
          } else {
            noParamComponents.push(component.code);
          }
        });

        setDisplayComponents(displayComponents);
        setNoParamComponents(noParamComponents);
        setOrgComponents(orgComponentsResponse);
      }
    } catch (error) {
      console.error("Error fetching organization components:", error);
    } finally {
      setLoading(false);
    }
  };

  //fetching account accessorials
  const fetchAccountComponentAccessorials = async () => {
    if (!accountId) return;

    try {
      setLoading(true);
      const result = await fetchAccountAccessorials(currentOrg.id, accountId, "DISPATCHER");
      if (result.success) {
        const accessorials = result.accessorials || [];
        setAccountComponentAccessorial(accessorials);
      } else {
        renderAlertMessage(result?.errors || ["Failed to fetch account accessorials"]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Email MOdal Render
  const renderEmailModal = () => {};

  // Send EDI Invoice
  const handleSendEdiInvoice = async () => {
    setSendingEdiInvoice(true);
    try {
      const payload = {
        account_invoice_ids: [accountInvoiceId],
        organization_id: currentOrg.id,
      };

      const response = await sendInvoiceEDIStatus(payload);

      if (response?.success) {
        const message = response?.msg || "EDI Invoice sent successfully";
        alertMessage(message);
      } else {
        renderAlertMessage(response?.errors || ["Failed to send EDI Invoice"]);
      }
    } catch (error) {
      console.error("Error in sending EDI status:", error);
      renderAlertMessage("An unexpected error occurred while sending EDI Invoice");
    } finally {
      setSendingEdiInvoice(false);
    }
  };

  //Added for after search showing 1st order detail from seached item
  const handleFilteredListChange = order => {
    if (!order) {
      // No search matches — clear selection so right panel shows the empty state
      setSelectedOrder(null);
      setCurrentOrderDetails(null);
      setCurrentOrderCharges(null);
      return;
    }

    const targetId = order._id || order.id;
    const currentId = selectedOrder ? selectedOrder._id || selectedOrder.id : null;
    if (targetId === currentId) return;

    if (hasPendingChangesRef.current) {
      if (hasInvalidCharges) {
        switchToOrder(order);
        handleDiscardAndSwitch();
        return;
      }
      setPendingTargetOrder(order);
      setIsUnsavedGuardVisible(true);
      return;
    }

    switchToOrder(order);
  };

  // Update just the total_amount for a given order in orderDetailsMap
  // without needing a full refetch — used right after a charges save.
  const handleOrderAmountUpdated = (customerOrderId, updatedAmount) => {
    if (!customerOrderId) return;

    setOrderDetailsMap(prev => {
      const existing = prev[customerOrderId] || {};
      return {
        ...prev,
        [customerOrderId]: {
          ...existing,
          customer_order_id: customerOrderId,
          total_amount: updatedAmount,
        },
      };
    });
  };

  return (
    <div className="invoice-charges-container">
      {isFetchingDetails ? (
        <div className="invoice-charges-body invoice-charges-body--skeleton">
          <div className="left-orders-panel-skeleton">
            {Array.from({ length: 6 }).map((_, idx) => (
              <div key={idx} className="left-orders-panel-skeleton__item">
                <Skeleton active paragraph={{ rows: 2 }} title={{ width: "60%" }} />
              </div>
            ))}
          </div>
          <div className="right-order-details-panel-skeleton">
            <div className="skeleton-line skeleton-line--title" />
            <div className="skeleton-line skeleton-line--wide" />
            <div className="skeleton-line skeleton-line--wide" />
            <div className="skeleton-line skeleton-line--medium" />
            <div className="skeleton-line skeleton-line--wide" style={{ marginTop: 24 }} />
            <div className="skeleton-line skeleton-line--wide" />
            <div className="skeleton-line skeleton-line--medium" />
          </div>
        </div>
      ) : (
        <>
          <div className="invoice-charges-header">
            <div className="invoice-charges-header-actions">
              {isGlobalEditMode ? (
                hasPendingChanges || hasInvalidCharges ? (
                  <Popover
                    content={
                      hasInvalidCharges
                        ? "Please fix the invalid charges before exiting edit mode."
                        : "You have unsaved changes. Please save or discard them before exiting edit mode."
                    }
                    trigger="hover"
                  >
                    <span>
                      <Button_v2
                        icon="check"
                        key="done-editing"
                        size="medium"
                        onClick={handleDoneEditing}
                        className="small-btn raised success-btn"
                        text="Done Editing"
                        disabled
                      />
                    </span>
                  </Popover>
                ) : (
                  <Button_v2
                    icon="check"
                    key="done-editing"
                    size="medium"
                    onClick={handleDoneEditing}
                    className="small-btn raised success-btn"
                    text="Done Editing"
                    disabled={rebillingProgress}
                  />
                )
              ) : (
                <Button_v2
                  icon="edit"
                  key="edit-order-charges"
                  size="medium"
                  onClick={handleEnableGlobalEditMode}
                  className="small-btn raised"
                  text="Edit Order Charges"
                  disabled={rebillingProgress}
                />
              )}

              <Button
                className="send-invoice-btn"
                onClick={callEmailApi}
                icon="mail"
                disabled={rebillingProgress}
              >
                Send Invoice
              </Button>

              {currentOrderDetails?.invoice_status === "APPROVED" && (
                <Button
                  className="send-edi-btn"
                  onClick={handleSendEdiInvoice}
                  disabled={sendingEdiInvoice || !accountInvoiceId || rebillingProgress}
                  icon="export"
                >
                  {sendingEdiInvoice ? "Sending..." : "Send EDI/QB Invoice"}
                </Button>
              )}
            </div>
          </div>
          <div className="invoice-charges-body">
            <LeftOrdersPanel
              invoice_orders={invoice_orders}
              selectedOrderId={currentOrderId}
              onOrderSelect={handleOrderSelect}
              orderDetailsMap={orderDetailsMap}
              onFilteredListChange={handleFilteredListChange} //Added for after search showing 1st order detail from seached item
              isFetchingDetails={isFetchingDetails}
              orderDetails={currentOrderDetails}
              rebillingProgress={rebillingProgress}
            />
            <RightOrderDetailsPanel
              ref={rightPanelRef}
              order={selectedOrder}
              orderDetails={currentOrderDetails}
              chargesDetails={currentOrderCharges}
              loading={loading}
              accountInvoiceId={accountInvoiceId}
              isEditMode={isGlobalEditMode}
              onEnableEditMode={handleDisableGlobalEditMode}
              accessorialList={accountComponentAccessorial}
              displayComponents={displayComponents}
              orgComponents={orgComponents}
              refreshCharges={() => fetchInvoiceCharges(selectedOrder)}
              onPendingChangesChange={value => {
                hasPendingChangesRef.current = value;
                setHasPendingChanges(value);
              }}
              setHasInvalidCharges={setHasInvalidCharges}
              drivers={drivers}
              isFetchingDetails={isFetchingDetails}
              onOrderAmountUpdated={handleOrderAmountUpdated}
              onChargesSaved={handleChargesModified} // NEW
              setRebillingProgress={setRebillingProgress}
              rebillingProgress={rebillingProgress}
            />
          </div>

          {/* Unsaved Changes guard modal - shown when switching orders with
          unsaved charge edits on the currently open order */}
          <Modal
            visible={isUnsavedGuardVisible}
            onCancel={closeUnsavedGuard}
            footer={null}
            closable={true}
            destroyOnClose
            width={450}
            className="unsaved-changes-modal"
            maskClosable={false}
          >
            <div className="unsaved-changes-modal__title">
              {" "}
              <Icon
                type="exclamation-circle"
                theme="filled"
                className="unsaved-changes-modal__icon"
              />
              Unsaved Changes
            </div>

            <div className="unsaved-changes-modal__message">
              You have unsaved changes for{" "}
              <span className="unsaved-changes-modal__order-number">
                Order #{getOrderNumber(selectedOrder)}
              </span>
              .<p>Please save or discard your changes before switching to another order.</p>
            </div>

            <div className="unsaved-changes-modal__footer">
              <Button_v2
                key="discard"
                size="medium"
                className="flat small-btn unsaved-changes-modal__discard-btn"
                onClick={handleDiscardAndSwitch}
                disabled={isGuardBusy}
                text="Discard"
              />
              <Button_v2
                key="save"
                size="medium"
                className="small-btn raised"
                onClick={handleSaveAndSwitch}
                loading={isGuardBusy}
                text="Save"
              />
            </div>
          </Modal>
        </>
      )}
    </div>
  );
};

InvoiceChargesContainer.propTypes = {
  invoice_orders: PropTypes.array,
  accountInvoiceId: PropTypes.string,
  currentOrg: PropTypes.object,
  accountId: PropTypes.string,
  isFetchingDetails: PropTypes.bool,
};

export default InvoiceChargesContainer;
